import './dashboard.scss';
import 'intro.js/introjs.css';

import { updateMerchant } from '@api';
import { Navbar, RingLoader, Sidebar } from '@basqet-dashboard/common/components';
import { PERMISSIONS } from '@basqet-dashboard/common/config';
import { useUserContext } from '@basqet-dashboard/common/context';
import { useHasPermission } from '@basqet-dashboard/common/hooks';
import { useFetchMerchantProfile } from '@basqet-dashboard/common/hooks/queries';
import { Cards, Disputes, Home, Transactions } from '@basqet-dashboard/common/pages';
import { FLAGS } from '@basqet-dashboard/common/utils';
import { MENU_ITEMS } from '@config';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

import {
  APIPage,
  Conversions,
  ConvertFunds,
  CorporateFIVerification,
  CorporateNonFIVerification,
  FreelancerVerification,
  MerchantWallets,
  Onboarding,
  PaymentLinks,
  PayoutRoutes,
  Settings,
  Settlements,
  TransactionDetailsPage
} from '../pages';
import { ErrorPage } from '../pages/404';

const MenuContext = createContext(MENU_ITEMS);

function Dashboard() {
  const onboardingEnabled = useFeatureFlagEnabled(FLAGS.ONBOARDING_V2);
  const { user } = useUserContext();
  // Drives the auth/refresh poll that populates permissions (and KYC
  // status) for every dashboard page — not just Home. Deduped with the
  // same query in NextSteps.
  const { isLoading: isProfileLoading } = useFetchMerchantProfile();
  const isKycVerified = user?.isKycVerified;

  // Hide onboarding when the feature flag is off or KYC is already approved
  const showOnboarding = onboardingEnabled !== false && !isKycVerified;

  // Hide legacy KYC settings tab when onboarding v2 is enabled
  const hideKycTab = onboardingEnabled !== false;

  const menuItems = useMemo(
    () =>
      MENU_ITEMS.map((item) => {
        if (item.name === 'Onboarding') return { ...item, hidden: !showOnboarding };
        if (item.name === 'Settings' && hideKycTab && item.tabs) {
          return {
            ...item,
            tabs: item.tabs.map((tab) => (tab.value === 'kyc' ? { ...tab, hidden: true } : tab))
          };
        }
        return item;
      }),
    [showOnboarding, hideKycTab]
  );

  // Permissions arrive from the polled auth/refresh query, not the OIDC
  // profile, so on a cold load `user.permissions` is briefly undefined.
  // Show a single boot loader instead of rendering the sidebar with a
  // partial menu or letting a route guard trip into a false "Page not
  // found". react-query's isLoading is true only on the first fetch (it
  // flips false once there's cached data), so this shows only on the
  // dashboard's initial mount — and not at all when permissions were
  // already seeded from session storage.
  const permissionsReady = Array.isArray(user?.permissions);
  if (isProfileLoading && !permissionsReady) {
    return (
      <div className="dashboard__boot-loader">
        <RingLoader />
      </div>
    );
  }

  return (
    <MenuContext.Provider value={menuItems}>
      <Router>
        <Switch>
          <DashboardRoute path="/" exact>
            <Home />
          </DashboardRoute>

          {showOnboarding && (
            <DashboardRoute path="/onboard" exact>
              <Onboarding />
            </DashboardRoute>
          )}

          {showOnboarding && (
            <DashboardRoute path="/onboard/freelancer" exact>
              <FreelancerVerification />
            </DashboardRoute>
          )}

          {showOnboarding && (
            <DashboardRoute path="/onboard/corporate_non_fi" exact>
              <CorporateNonFIVerification />
            </DashboardRoute>
          )}

          {showOnboarding && (
            <DashboardRoute path="/onboard/corporate_fi" exact>
              <CorporateFIVerification />
            </DashboardRoute>
          )}

          <DashboardRoute path="/cards" exact>
            <Cards />
          </DashboardRoute>

          <DashboardRoute
            path="/transactions/:transactionId"
            exact
            requiredPermission={PERMISSIONS.TRANSACTIONS_VIEW}>
            <TransactionDetailsPage />
          </DashboardRoute>

          <DashboardRoute
            path="/transactions"
            exact
            requiredPermission={PERMISSIONS.TRANSACTIONS_VIEW}>
            <Transactions />
          </DashboardRoute>

          <DashboardRoute path="/wallets/:tab?/:id?" requiredPermission={PERMISSIONS.BALANCE_VIEW}>
            <MerchantWallets />
          </DashboardRoute>

          <DashboardRoute path="/settlement" requiredPermission={PERMISSIONS.SETTLEMENTS_VIEW}>
            <Settlements />
          </DashboardRoute>

          <DashboardRoute path="/payment-links" requiredPermission={PERMISSIONS.PAYMENT_LINKS_VIEW}>
            <PaymentLinks />
          </DashboardRoute>

          <DashboardRoute path="/settings/:tab?/:type?">
            <Settings />
          </DashboardRoute>

          <DashboardRoute path="/api" requiredPermission={PERMISSIONS.DEVELOPER_TAB_VIEW}>
            <APIPage />
          </DashboardRoute>

          <DashboardRoute path="/disputes" requiredPermission={PERMISSIONS.DISPUTES_VIEW}>
            <Disputes />
          </DashboardRoute>

          {/* Convert flow is a sub-route under /conversions; declare it
              BEFORE the list route so React Router v5's Switch picks it. */}
          <DashboardRoute
            path="/conversions/convert"
            exact
            requiredPermission={PERMISSIONS.WALLET_CONVERSIONS_MANAGE}>
            <ConvertFunds />
          </DashboardRoute>
          <DashboardRoute
            path="/conversions/:tab?"
            requiredPermission={PERMISSIONS.WALLET_CONVERSIONS_VIEW}>
            <Conversions />
          </DashboardRoute>
          <PayoutRoutes />
          <Route path="*" component={ErrorPage}></Route>
        </Switch>
      </Router>
    </MenuContext.Provider>
  );
}

const DashboardRoute = ({ children, requiredPermission, ...props }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasPermission = useHasPermission();
  const menuItems = useContext(MenuContext);

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <ErrorPage />;
  }

  const closeMenu = () => setIsMenuOpen(false);
  const openMenu = () => setIsMenuOpen(true);
  return (
    <>
      <main className="dashboard">
        <Sidebar isMenuOpen={isMenuOpen} closeMenu={closeMenu} menuItems={menuItems} />
        <div className="dashboard__content">
          <Navbar openSideBar={openMenu} menuItems={menuItems} updateMerchant={updateMerchant} />
          <Route {...props}>{children}</Route>
        </div>
      </main>
    </>
  );
};

DashboardRoute.propTypes = {
  children: PropTypes.any,
  requiredPermission: PropTypes.string
};

export { Dashboard, DashboardRoute, MenuContext };
